import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import SvgIcon from '@mui/material/SvgIcon';
import { useEffect, useState } from 'react';
import Chip from '@mui/material/Chip';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { PulseLoader } from 'react-spinners';
import { getTimezone } from '@mgm/common/utils';
import {
  DashboardExpenses,
  DashboardLocationSales,
  DashboardSalesByEmployees,
  DashboardLatestProducts,
  DashboardTransactions,
  DashboardTransactionsPie,
  DashboardTimesheets,
} from '../features/dashboard';
import { Loader, PageHelmet } from '../components';
import { PAGES_ANALYTICS_TITLES } from '../utils/enums';
import { useGetDashboardDailyStateAPI } from '../hooks/api/useDashboard';
import { useAppDispatch, useAppSelector } from '../hooks/useAppStore';
import { dashboardActions } from '../store/dashboard/slice';
import { getPreset } from '../store/dashboard/selectors';
import { ReactComponent as CheckIcon } from '../static/icons/ic_outline_check.svg';

const DashboardPage = () => {
  const dispatch = useAppDispatch();
  const preset = useAppSelector(getPreset);
  const { t } = useTranslation(['schedule', 'dashboard', 'orders']);
  const [chosenDates, setChosenDates] = useState({ fromDate: '', toDate: '' });

  const { refetch, isLoading: isDailyLoading, isRefetching } = useGetDashboardDailyStateAPI({
    dates: chosenDates,
  });

  useEffect(() => {
    dispatch(dashboardActions.setPreset('today'));
  }, []);

  const getDateRange = (preset: string) => {
    const timezone = getTimezone();

    let fromDate = moment.tz(timezone).startOf('day');
    let toDate = moment.tz(timezone).endOf('day');

    switch (preset) {
      case 'today':
        fromDate = moment.tz(timezone).startOf('day');
        toDate = moment.tz(timezone).endOf('day');
        break;
      case 'last-3-days':
        fromDate = moment.tz(timezone).subtract(3, 'days').startOf('day'); // Start of 3 days ago
        toDate = moment.tz(timezone).endOf('day'); // End of today
        break;
      case 'last-week':
        fromDate = moment.tz(timezone).subtract(1, 'week').startOf('isoWeek'); // Start of the previous week
        toDate = moment.tz(timezone).subtract(1, 'week').endOf('isoWeek'); // End of the previous week
        break;
      case 'last-7-days':
        fromDate = moment.tz(timezone).subtract(7, 'days').startOf('day'); // Start of the last 7 days
        toDate = moment.tz(timezone).endOf('day'); // End of today
        break;
      case 'current-month':
        fromDate = moment.tz(timezone).startOf('month'); // Start of the current month
        toDate = moment.tz(timezone).endOf('month'); // End of the current month
        break;
      default:
        break;
    }

    setChosenDates({
      fromDate: fromDate.format(),
      toDate: toDate.format(),
    });
  };

  const shortcutsItems = [
    {
      label: t('today'),
      value: 'today',
    },
    {
      label: t('lastThreeDays', { ns: 'dashboard' }),
      value: 'last-3-days',
    },
    {
      label: t('lastWeek', { ns: 'dashboard' }),
      value: 'last-week',
    },
    {
      label: t('lastSevenDays', { ns: 'dashboard' }),
      value: 'last-7-days',
    },
    {
      label: t('currentMonth', { ns: 'dashboard' }),
      value: 'current-month',
    },
  ];

  const handleChipClick = async (preset: string) => {
    dispatch(dashboardActions.setPreset(preset));

    getDateRange(preset);
    setTimeout(async () => {
      await refetch();
    }, 400);
  };

  if (isDailyLoading) {
    return <Loader isDashboard />;
  }

  return (
    <PageHelmet title={PAGES_ANALYTICS_TITLES.DASHBOARD}>
      <Container
        sx={{
          px: { xs: 1, lg: 2 },
        }}
        maxWidth={false}
      >
        <Grid container>
          <Grid
            pt={2}
            size={{
              sm: 12, xs: 12, md: 8, lg: 6,
            }}
          >
            <Box>
              {shortcutsItems?.map((item) => (
                <Chip
                  key={item.value}
                  sx={{
                    backgroundColor: preset === item.value ? '#A855F7' : 'defaultBackgroundColor',
                    color: preset === item.value ? 'white' : '#A855F7',
                    '&:hover': {
                      backgroundColor: '#A855F7',
                      color: 'white',
                    },
                    '& .MuiChip-label': {
                      color: 'inherit',
                    },
                  }}
                  label={item.label}
                  onClick={() => handleChipClick(item?.value)}
                  style={{ margin: '4px' }}
                />
              ))}
            </Box>
          </Grid>
          <Grid
            pt={2}
            justifyContent="flex-end"
            display="flex"
            size={{
              sm: 12, xs: 12, md: 4, lg: 6,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: isRefetching ? 'inherit' : 'rgb(16 185 129 / 1)',
              }}
            >
              {isRefetching ? <PulseLoader color="#A855F7" size={8} /> : (
                <SvgIcon
                  sx={{
                    fill: 'none',
                  }}
                  component={CheckIcon}
                  inheritViewBox
                />
              )}
              <Box ml={1}>
                <Typography color={isRefetching
                  ? 'primary' : 'rgb(16 185 129 / 1)'}
                >
                  {isRefetching ? t('loading', { ns: 'dashboard' }) : t('updated', { ns: 'orders' })}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid
            pt={2}
            size={{
              sm: 12, xs: 12, md: 12, lg: 12,
            }}
          >
            <DashboardExpenses />
          </Grid>
          <Grid
            size={{
              sm: 12, xs: 12, md: 6, lg: 6,
            }}
          >
            <DashboardLatestProducts />
          </Grid>
          <Grid size={{
            sm: 12, xs: 12, md: 6, lg: 6,
          }}
          >
            <DashboardSalesByEmployees />
          </Grid>
          <Grid size={{
            sm: 12, xs: 12, md: 6, lg: 6,
          }}
          >
            <DashboardLocationSales />
          </Grid>
          <Grid
            pb={1}
            size={{
              sm: 12, xs: 12, md: 6, lg: 6,
            }}
          >
            <DashboardTimesheets />
            <DashboardTransactions />
          </Grid>
          <Grid size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
          >
            <DashboardTransactionsPie />
          </Grid>
        </Grid>
      </Container>
    </PageHelmet>
  );
};

export default DashboardPage;
